import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EyeOutlined from '@ant-design/icons/lib/icons/EyeOutlined';
import MinusCircleOutlined from '@ant-design/icons/lib/icons/MinusCircleOutlined';
import PlusOutlined from '@ant-design/icons/lib/icons/PlusOutlined';

import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { useAddQueryParams } from 'utils/customHooks/useAddQueryParams';
import { useRemoveQueryParams } from 'utils/customHooks/useRemoveQueryParams';
import { redFlagReadScopes } from 'constants/visitorScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumSend } from 'utils/helpers';
import { Loader } from 'components/Loader';
import { RequestLicensesModal } from 'new-components/RequestLicenses/RequestLicensesModal';

import { visitorSmallPackageId } from '../../../../../constants/packageIds';
import {
  fetchRedFlags,
  RedFlagState,
} from '../../../../../store/slices/redFlagSlice';
import { searchVisitorsToday } from '../../../../../store/slices/visitorSearchSlice';
import {
  fetchVisits,
  setSelectedVisitor,
  VisitsState,
} from '../../../../../store/slices/visitsSlice';
import { AppDispatch, ImmutableRootState } from '../../../../../store/store';
import { LocationsState } from '../../../../../store/slices/locationsSlice';
import STATUS from '../../../../../constants/status';
import warningIcon from '../../../../../images/icons/warning.svg';
import { Visitor } from '../../../../../types/visitTypes';
import { formatFullName, statusKeyMap } from '../../../../../utils/utils';
import {
  getStatusColor,
  getStatusNameById,
  isVisitorRedFlagged,
  profileIcon,
} from '../../../../../utils/visitorsHelper';
import { useVisitorFeature } from '../../../../../hooks/useVisitorFeature';

import VisitorListModal, {
  VisitorListModalSubmitData,
} from './VisitorListModal';

const VisitorList = () => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const addQueryParams = useAddQueryParams();
  const removeQueryParams = useRemoveQueryParams();
  const { isValid: hasAllvisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasInviteWrite } = useValidateScopes([
    'o{orgId}-visitorInvite:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasRedFlagRead } = useValidateScopes(redFlagReadScopes);
  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const redFlags = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['redFlags', 'redFlags']),
  ) as RedFlagState['redFlags'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const locationCount = Array.isArray(locations) ? locations.length : 0;

  const { totalLicenses } = useVisitorFeature(orgId);

  const licensesLeft = totalLicenses - locationCount;

  const [isVisitorListModalOpen, setIsVisitorListModalOpen] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isRequestLicensesModalOpen, setIsRequestLicensesModalOpen] =
    useState(false);

  const confirmModal = useConfirmModal();

  const checkLicenseAndProceed = (callback: () => void) => {
    if (locationCount > totalLicenses) {
      confirmModal({
        title: t('License limit reached'),
        content: `You have ${Math.max(0, licensesLeft)} licenses remaining.`,
        okText: t('Request licenses'),
        onOk: () => setIsRequestLicensesModalOpen(true),
      });
      return;
    }

    callback();
  };

  const showProfileDrawer = (visitor: Visitor) => {
    dispatch(setSelectedVisitor(visitor));
    addQueryParams({ visitorId: String(visitor.id) });
  };

  // Guarded on redFlagReadScopes because the helium GET /redflag route rejects
  // roles without red flag read (e.g. dashboard-only) with a 403.
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (hasRedFlagRead && redFlags.length === 0) {
          dispatch(fetchRedFlags({ orgId }));
        }
      } catch {
        // Failed to fetch data
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, orgId, hasRedFlagRead, redFlags.length]);

  const addVisitor = (visitor: VisitorListModalSubmitData) => {
    // Add status field to the visitor object
    const visitorWithStatus = { ...visitor, status: STATUS.PENDING.id };
    const visitors = { visitors: [visitorWithStatus] };
    const addVisitorsToVisit = async () => {
      try {
        await heliumSend('addVisitors', [orgId, selectedVisit?.id], visitors);
        notificationService.show(
          'success',
          t('visitors.visitorList.addSuccess'),
        );
        dispatch(fetchVisits({ orgId }));
      } catch {
        notificationService.show('error', t('visitors.visitorList.addFail'));
      } finally {
        setIsVisitorListModalOpen(false);
        setLoading(false);
      }
    };
    addVisitorsToVisit();
  };

  const deleteVisitor = (visitorId: number) => {
    if (selectedVisit?.visitors.length && selectedVisit?.visitors.length > 1) {
      confirmModal({
        title: t('visitors.visitorList.confirmDeleteVisitor.title'),
        content: t('visitors.visitorList.confirmDeleteVisitor.message'),
        okText: t('visitors.visitorList.confirmDeleteVisitor.ok'),
        cancelText: t('visitors.visitorList.confirmDeleteVisitor.cancel'),
        onOk: () => {
          const removeVisitorFromVisit = async () => {
            setLoading(true);
            try {
              await heliumSend(
                'removeVisitors',
                [orgId, selectedVisit.id, visitorId],
                {},
              );
              notificationService.show(
                'success',
                t('visitors.visitorList.deleteSuccess'),
              );
              dispatch(fetchVisits({ orgId }));
              dispatch(searchVisitorsToday({ orgId }));
              removeQueryParams(['visitorId']);
            } catch {
              notificationService.show(
                'error',
                t('visitors.visitorList.deleteFail'),
              );
            } finally {
              setLoading(false);
            }
          };
          removeVisitorFromVisit();
        },
      });
    } else {
      notificationService.show('error', t('visitors.visitorList.mustHaveOne'));
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <OpList
            dataSource={selectedVisit?.visitors}
            renderItem={(item) => {
              const visitor = item as Visitor;
              const actions =
                selectedVisit?.visitStatus.id === 1 &&
                hasInviteWrite &&
                (hasAllvisitorsWrite ||
                  globalUserId === selectedVisit?.host.userId)
                  ? [
                      <EyeOutlined
                        onClick={() => showProfileDrawer(visitor)}
                        key={`a-${visitor.id}`}
                      />,
                      <MinusCircleOutlined
                        onClick={() => deleteVisitor(visitor.id!)}
                        key={`d-${visitor.id}`}
                      />,
                    ]
                  : [
                      <EyeOutlined
                        onClick={() => showProfileDrawer(visitor)}
                        key={`a-${visitor.id}`}
                      />,
                    ];
              return (
                <OpList.Item
                  key={visitor.id}
                  actions={actions}
                  onClick={() => showProfileDrawer(visitor)}
                  style={{ cursor: 'pointer' }}
                >
                  <OpList.Item.Meta
                    avatar={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '48px',
                        }}
                      >
                        {isVisitorRedFlagged(visitor, redFlags) ? (
                          <img
                            src={warningIcon}
                            alt="Warning"
                            style={{
                              width: '36px',
                              height: '36px',
                              margin: 'auto',
                            }}
                          />
                        ) : (
                          <div style={{ margin: 'auto' }}>
                            {profileIcon({
                              visitor,
                              size: 36,
                              color: getStatusColor(
                                getStatusNameById(
                                  visitor.status!,
                                  selectedVisit?.scheduleStart,
                                  selectedVisit?.location?.timezone,
                                ),
                              ),
                            })}
                          </div>
                        )}
                      </div>
                    }
                    title={
                      <span key={`a-${visitor.id}`}>
                        {formatFullName(
                          visitor.firstName,
                          visitor.middleName,
                          visitor.lastName,
                        )}
                      </span>
                    }
                    description={
                      <span>
                        <span
                          style={{
                            color: getStatusColor(
                              getStatusNameById(
                                visitor.status!,
                                selectedVisit?.scheduleStart!,
                                selectedVisit?.location?.timezone,
                              ),
                            ),
                          }}
                        >
                          {t(
                            statusKeyMap[
                              getStatusNameById(
                                visitor.status!,
                                selectedVisit?.scheduleStart!,
                                selectedVisit?.location?.timezone,
                              )
                            ] ||
                              getStatusNameById(
                                visitor.status!,
                                selectedVisit?.scheduleStart!,
                                selectedVisit?.location?.timezone,
                              ),
                          )}
                        </span>
                        {` |  ${t('visitors.visitorList.email')}: ${visitor.email || 'N/A'} | ${t('visitors.visitorList.mobilePhone')}: ${visitor.mobilePhone || 'N/A'}`}
                      </span>
                    }
                  />
                </OpList.Item>
              );
            }}
          />
          {selectedVisit?.visitStatus.id === 1 &&
            hasInviteWrite &&
            (hasAllvisitorsWrite ||
              globalUserId === selectedVisit?.host.userId) && (
              <OpButton
                type="dashed"
                onClick={() =>
                  checkLicenseAndProceed(() => setIsVisitorListModalOpen(true))
                }
                block
                icon={<PlusOutlined />}
              >
                {t('visitors.visitorList.add')}
              </OpButton>
            )}
        </>
      )}

      {isVisitorListModalOpen && (
        <VisitorListModal
          open={isVisitorListModalOpen}
          onOk={addVisitor}
          onClose={() => setIsVisitorListModalOpen(false)}
        />
      )}
      <RequestLicensesModal
        orgId={orgId}
        featureName={t('Visitor')}
        modalOpen={isRequestLicensesModalOpen}
        onClose={() => setIsRequestLicensesModalOpen(false)}
        packageId={visitorSmallPackageId}
      />
    </>
  );
};

export default VisitorList;
